import {Box, Grid, ThemeProvider} from '@primer/react-brand/esm'
import {WebGLPage} from '@github-ui/brand-webgl'
import {useMemo} from 'react'
import {
  buildPersonalizationValue,
  PersonalizationProvider,
  type PersonalizationUserStateProps,
} from '@github-ui/swp-core/contexts/PersonalizationContext'
import type {FlexSuiteDetailsPage} from '../types'
import {DetailSection} from './components/DetailSection'
import {FlexSuiteHero} from '../components/FlexSuiteHero'
import {FlexSuiteSubNav} from '../components/FlexSuiteSubNav'
import {IntroSection} from './components/IntroSection'
import {OutroSection} from './components/OutroSection'
import baseStyles from '../styles/base.module.css'
import templateVarsStyles from '../styles/variables.module.css'

export type DetailsTemplateProps = {
  page: FlexSuiteDetailsPage
} & PersonalizationUserStateProps

export function DetailsTemplate({
  page,
  userLoggedIn,
  userCopilotPlan,
  orgMembershipType,
  orgAdminType,
}: DetailsTemplateProps) {
  const personalizationValue = useMemo(
    () =>
      buildPersonalizationValue({
        userLoggedIn,
        userCopilotPlan,
        orgMembershipType,
        orgAdminType,
      }),
    [userLoggedIn, userCopilotPlan, orgMembershipType, orgAdminType],
  )

  return (
    <PersonalizationProvider value={personalizationValue}>
      <DetailsTemplateContent page={page} />
    </PersonalizationProvider>
  )
}

function DetailsTemplateContent({page}: Pick<DetailsTemplateProps, 'page'>) {
  const {template, settings} = page.fields
  const pageColorMode = settings?.fields.colorMode ?? 'light'
  const {
    settings: templateSettings,
    subNav,
    heroHeading,
    heroLabel,
    heroDescription,
    heroCtaOne,
    heroCtaTwo,
    heroTertiaryDescription,
    heroVariant,
    heroAsset,
    heroPosition,
    heroCarousel,
    introLogoHeading,
    introLogoType,
    introLogos,
    introLogoLink,
    introContext,
    introEyebrow,
    introContextHeading,
    introContextLink,
    introContextBody,
    introItems,
    introIconCards,
    introPillars,
    introImageCards,
    introStats,
    outroTestimonial,
    outroTestimonialLink,
    outroTestimonialLogo,
    outroTestimonialQuote,
    outroTestimonialImage,
    outroTestimonialLabel,
    outroTestimonialStats,
    outroTestimonialName,
    outroTestimonialTitle,
    outroCustomerStories,
    outroCtaBanner,
    outroCtaHeading,
    outroCtaDescription,
    outroCtaOne,
    outroCtaTwo,
    outroIncludeResources,
    outroResourcesHeading,
    outroResources,
    outroHeading,
    outroFaqs,
  } = template.fields

  // Details pages keep stacked heroes on the regular centered gridline layout;
  // only the carousel variant uses the expressive split treatment here.
  const isExpressive = heroVariant === 'Carousel'
  const isFullBleedStackedHero = heroVariant === 'Stacked' && heroPosition === 'Fill'
  const showHeroDither = !isFullBleedStackedHero
  // The text-only Default hero (and the unset variant fallback) renders no
  // bottom border, so a following marquee LogoSuite needs its own top
  // gridline border to keep the section frame visually continuous.
  const hasMarqueeTopBorder = heroVariant === 'Default' || heroVariant == null

  const heroContent = (
    <FlexSuiteHero
      heroHeading={heroHeading}
      heroLabel={heroLabel}
      heroDescription={heroDescription}
      heroCtaOne={heroCtaOne}
      heroCtaOnePersonalisation={templateSettings?.fields.heroCtaOnePersonalisation}
      heroCtaTwo={heroCtaTwo}
      heroCtaTwoPersonalisation={templateSettings?.fields.heroCtaTwoPersonalisation}
      heroTertiaryDescription={heroTertiaryDescription}
      heroVariant={heroVariant}
      heroAsset={heroAsset}
      heroPosition={heroPosition}
      heroCarousel={heroCarousel}
      brand={templateSettings?.fields.brand}
      showDither={showHeroDither}
      options={{
        variant: isExpressive ? 'gridline-expressive' : 'gridline',
        align: isExpressive ? 'start' : 'center',
        enableAnimation: true,
        glassBorder: heroVariant === 'Stacked' && (heroPosition == null || heroPosition === 'Default'),
        textVariant: heroVariant === '50/50' ? 'default' : 'muted',
      }}
    />
  )

  return (
    <ThemeProvider colorMode={pageColorMode}>
      {/* The Box component is necessary to set the right background color according to the theme's color mode */}
      <Box backgroundColor="default" className={templateVarsStyles.templateVars}>
        <WebGLPage mode={pageColorMode}>
          {subNav && <FlexSuiteSubNav subNav={subNav} />}
          {heroVariant === '50/50' ? (
            <>
              <Box className={baseStyles.gridFrameBottom}>
                <Grid enableGutters={false} columnGap="none" rowGap="none">
                  <Grid.Column>
                    <Box backgroundColor="subtle">{heroContent}</Box>
                  </Grid.Column>
                </Grid>
              </Box>
              <Box paddingBlockStart="spacious" className={baseStyles.gridFrameBottom} />
            </>
          ) : (
            heroContent
          )}
          <IntroSection
            introLogoHeading={introLogoHeading}
            introLogoType={introLogoType}
            introLogos={introLogos}
            introLogoLink={introLogoLink}
            settings={templateSettings}
            introContext={introContext}
            introEyebrow={introEyebrow}
            introContextHeading={introContextHeading}
            introContextLink={introContextLink}
            introContextBody={introContextBody}
            introItems={introItems}
            introIconCards={introIconCards}
            introPillars={introPillars}
            introImageCards={introImageCards}
            introStats={introStats}
            logobarOptions={{marqueeSpeed: 'slow', variant: 'muted'}}
            logoSuiteOptions={{variant: 'gridline-expressive'}}
            hasMarqueeTopBorder={hasMarqueeTopBorder}
          />
          {template.fields.featuredSections?.map(section => (
            <DetailSection key={section.sys.id} {...section.fields} settings={templateSettings} />
          ))}
          <OutroSection
            settings={templateSettings}
            outroTestimonial={outroTestimonial}
            outroTestimonialLink={outroTestimonialLink}
            outroTestimonialLogo={outroTestimonialLogo}
            outroTestimonialQuote={outroTestimonialQuote}
            outroTestimonialImage={outroTestimonialImage}
            outroTestimonialLabel={outroTestimonialLabel}
            outroTestimonialStats={outroTestimonialStats}
            outroTestimonialName={outroTestimonialName}
            outroTestimonialTitle={outroTestimonialTitle}
            outroCustomerStories={outroCustomerStories}
            outroCtaBanner={outroCtaBanner}
            outroCtaHeading={outroCtaHeading}
            outroCtaDescription={outroCtaDescription}
            outroCtaOne={outroCtaOne}
            outroCtaTwo={outroCtaTwo}
            outroIncludeResources={outroIncludeResources}
            outroResourcesHeading={outroResourcesHeading}
            outroResources={outroResources}
            outroHeading={outroHeading}
            outroFaqs={outroFaqs}
          />
        </WebGLPage>
      </Box>
    </ThemeProvider>
  )
}
